import { useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useDocumentLink from "@/hooks/useDocumentLink";
import { DocumentLink } from "@/models/document-link/DocumentLink";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import { getEffectiveDivFields } from "../utils/documentLinkDivUtils";
import {
  buildAppNavigationUrl,
  openAppNavigation,
  type NavigationContext,
} from "@/lib/navigation/appNavigation";

/**
 * Shared map for "no links", so a document without links keeps one map (and
 * one set of resolver callbacks) from the loading state through the empty
 * response. A new map there re-parsed the whole document (LAR-487).
 */
const NO_DOCUMENT_LINKS = new Map<string, DocumentLink>();

export function useDocumentLinks(currentDocId?: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const { useQueryByDocumentId } = useDocumentLink();
  // Select the one action: subscribing to the whole persisted store re-rendered
  // the renderer on every unrelated store update.
  const resetWorkspaceDocumentVersionStore = useWorkspaceDocumentVersionStore(
    (s) => s.resetWorkspaceDocumentVersionStore
  );

  const navigationContext: NavigationContext = location.pathname.includes(
    "/chat/"
  )
    ? "chat"
    : "app";

  // Fetch document links when currentDocId is available
  const { data, isLoading: isLoadingDocumentLinks } = useQueryByDocumentId(
    currentDocId || "",
    {
      enabled: !!currentDocId,
    }
  );
  // An empty response means the same as "not loaded yet" to every consumer.
  const documentLinks = data?.length ? data : undefined;

  // Create memoized map of document links keyed by htmlId + GUID for flexibility
  const documentLinksMap = useMemo(() => {
    if (!documentLinks) return NO_DOCUMENT_LINKS;
    const map = new Map<string, DocumentLink>();
    for (const link of documentLinks) {
      // Also store by link.id (GUID) for direct lookup
      map.set(link.id, link);

      // Map by effective HTML ids for the open document (origin or destination)
      const { htmlIds } = getEffectiveDivFields(link, currentDocId);
      if (htmlIds && htmlIds.length > 0) {
        for (const htmlId of htmlIds) {
          map.set(htmlId, link);
          const guidMatch = htmlId.match(/^doclink_([a-f0-9-]+)_\d+$/i);
          if (guidMatch) {
            map.set(guidMatch[1], link);
          }
        }
      }
    }
    return map;
  }, [documentLinks, currentDocId]);

  const extractDocumentLinkKeys = useCallback(
    (attrs: { id?: string | null }) => {
      const keys: string[] = [];

      if (attrs.id) {
        keys.push(attrs.id);
        const idMatch = attrs.id.match(/^doclink_([a-f0-9-]+)_\d+$/i);
        if (idMatch) {
          keys.push(idMatch[1]);
        }
      }

      return keys;
    },
    []
  );

  const resolveDocumentLink = useCallback(
    (keys: string[]) => {
      for (const key of keys) {
        const link = documentLinksMap.get(key);
        if (link) {
          return { linkId: key, link };
        }
      }
      return null;
    },
    [documentLinksMap]
  );

  const buildDocumentLinkHref = useCallback(
    (link: DocumentLink): string | undefined => {
      const url = buildAppNavigationUrl({
        kind: "documentLink",
        link,
        currentDocumentId: currentDocId,
        context: navigationContext,
      });
      return url ?? undefined;
    },
    [currentDocId, navigationContext]
  );

  const navigateToDocumentLink = useCallback(
    (linkId: string, event?: React.MouseEvent | MouseEvent) => {
      const link = documentLinksMap.get(linkId);
      if (!link) {
        logger.warn("Clicked document link is not among the loaded links", {
          operation: "navigateToDocumentLink",
          linkId,
          documentId: currentDocId,
        });
        showErrorToast("Kunne ikke finde dokumentlink");
        return;
      }

      const opened = openAppNavigation({
        target: {
          kind: "documentLink",
          link,
          currentDocumentId: currentDocId,
          context: navigationContext,
        },
        navigate,
        event,
        onWorkspaceDocumentNavigate: resetWorkspaceDocumentVersionStore,
      });

      if (!opened) {
        logger.warn("Could not resolve where a document link points", {
          operation: "navigateToDocumentLink",
          linkId,
          documentId: currentDocId,
        });
        showErrorToast("Kunne ikke bestemme dokument type");
      }
    },
    [
      documentLinksMap,
      navigate,
      resetWorkspaceDocumentVersionStore,
      currentDocId,
      navigationContext,
    ]
  );

  return {
    documentLinks,
    documentLinksMap,
    isLoadingDocumentLinks,
    extractDocumentLinkKeys,
    resolveDocumentLink,
    navigateToDocumentLink,
    buildDocumentLinkHref,
  };
}
